import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useSnackbar, type SnackbarKey } from 'notistack';

import { Button } from '@/components/ui/button';
import { listAudioOverviews } from '@/services/audioOverviews';
import {
  audioOverviewsReset,
  audioOverviewsUpserted,
  audioOverviewUpgradePromptClosed,
  nextAudioOverviewStamp,
  selectAudioOverviewConfigState,
  selectAudioOverviewEntities,
  selectAudioOverviewListEpoch,
  selectAudioOverviewUpgradePromptOpen,
  selectAudioOverviewUpgradePromptSourceKind,
  selectPendingAudioOverviews,
} from '@/slices/audioOverviews/audioOverviewsSlice';
import type { RootState } from '@/store/store';
import {
  isAudioOverviewTerminal,
  type AudioOverviewStatus,
  type AudioOverviewSummary,
} from '@/types/AudioOverview';
import {
  getAudioOverviewPlayer,
  setAudioOverviewProgressHandler,
  type AudioOverviewPlayer,
} from '../player/audioOverviewPlayer';
import {
  createAudioOverviewProgressHandler,
  promptAudioOverviewUpgrade,
} from '../audioOverviewPlayback';
import {
  loadAudioOverviewConfig,
  resetAudioOverviewConfigCache,
  useAudioOverviewConfig,
} from '../hooks/useAudioOverviewConfig';
import { useAudioOverviewActions } from '../hooks/useAudioOverviewActions';
import { audioOverviewRowTitle } from '../list/AudioOverviewRow';
import { useAudioOverviewConfigUpkeep } from './useAudioOverviewConfigUpkeep';
import { useAudioOverviewPolling } from './useAudioOverviewPolling';

export { AUDIO_OVERVIEW_POLL_INTERVAL_MS } from './useAudioOverviewPolling';

const UpgradeModal = lazy(
  () => import('@/components/ora/Upgrade/UpgradeModal')
);

/** Enough to find anything still generating when the app boots. */
const BOOT_FETCH_LIMIT = 20;
const TOAST_DURATION_MS = 10_000;

interface AudioOverviewWatcherProps {
  /** Defaults to the app-wide engine; tests pass a fake. */
  player?: AudioOverviewPlayer;
}

/**
 * The app-wide half of Audio Overviews (ORA-735), mounted beside the router so
 * it keeps working after the student leaves Preclinical:
 *
 * - finds anything still generating once on boot, then polls each one every
 *   5 s, only while something is generating;
 * - toasts READY (with Play) and FAILED (with Retry) for overviews it watched
 *   finish this session;
 * - saves resume positions from the engine;
 * - hosts the paywall every Audio Overview surface opens.
 *
 * Renders nothing and asks nothing while signed out or while the feature is
 * off (`/config` 404).
 */
export const AudioOverviewWatcher = ({ player }: AudioOverviewWatcherProps) => {
  const isSignedIn = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  const userId = useSelector(
    (state: RootState) => state.user?.profile?.id ?? null
  );

  const dispatch = useDispatch();
  const previousUserIdRef = useRef<string | null>(userId);

  // Another account without a logout in between (impersonation, a login
  // over a stale session): nothing of the last one may carry over.
  useEffect(() => {
    const previous = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    if (previous && userId && previous !== userId) {
      resetAudioOverviewConfigCache();
      dispatch(audioOverviewsReset());
    }
  }, [dispatch, userId]);

  return isSignedIn ? (
    <SignedInWatcher key={userId ?? 'signed-in'} player={player} />
  ) : null;
};

const SignedInWatcher = ({ player }: AudioOverviewWatcherProps) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { isAvailable } = useAudioOverviewConfig();
  const configState = useSelector(selectAudioOverviewConfigState);
  const listEpoch = useSelector(selectAudioOverviewListEpoch);
  const actions = useAudioOverviewActions(player);
  const isUpgradeOpen = useSelector(selectAudioOverviewUpgradePromptOpen);

  const upgradeSourceKind = useSelector(
    selectAudioOverviewUpgradePromptSourceKind
  );

  const entities = useSelector(selectAudioOverviewEntities);
  const pending = useSelector(selectPendingAudioOverviews);
  const lastStatusRef = useRef(new Map<string, AudioOverviewStatus>());

  const pendingKey = useMemo(
    () =>
      pending
        .map((overview) => overview.id)
        .sort()
        .join(','),
    [pending]
  );

  const showToast = (
    overview: AudioOverviewSummary,
    kind: 'ready' | 'failed'
  ) => {
    const keyRef: { current: SnackbarKey | null } = { current: null };
    const title = audioOverviewRowTitle(overview);

    const close = () => {
      if (keyRef.current !== null) closeSnackbar(keyRef.current);
    };

    const message = (
      <div className="flex items-center gap-3">
        <span>
          {kind === 'ready'
            ? `Your audio overview “${title}” is ready.`
            : 'Your audio overview couldn’t be generated.'}
        </span>
        {overview.locked ||
        (kind === 'failed' && !overview.retryable) ? null : (
          <Button
            size="sm"
            context="modal"
            className="shrink-0"
            onClick={() => {
              close();
              if (kind === 'ready') actions.play(overview);
              else void actions.retry(overview);
            }}
          >
            {kind === 'ready' ? 'Play' : 'Retry'}
          </Button>
        )}
      </div>
    );

    keyRef.current = enqueueSnackbar(message, {
      autoHideDuration: TOAST_DURATION_MS,
    });
  };

  // The plan lapsed while an episode is loaded: stop it and say why, once
  // (closing it clears the track, so a second signal finds nothing to do).
  const stopForPlan = (
    source: 'plan_changed' | 'playback_rejected',
    trackId?: string
  ) => {
    const engine = player ?? getAudioOverviewPlayer();
    const loaded = engine.getState().track;
    if (!loaded || (trackId && loaded.id !== trackId)) return;
    engine.close();

    promptAudioOverviewUpgrade(dispatch, source, {
      sourceKind: loaded.sourceKind,
      articleId: loaded.articleId,
    });
  };

  const stopForPlanRef = useRef(stopForPlan);
  stopForPlanRef.current = stopForPlan;

  // Resume positions go to the server for as long as someone is signed in.
  useEffect(() => {
    const handler = createAudioOverviewProgressHandler({
      dispatch,
      getOverview: (id) => store.getState().audioOverviews.entities[id],
      onUpgradeRequired: (trackId) =>
        stopForPlanRef.current('playback_rejected', trackId),
    });

    if (player) player.setProgressHandler(handler);
    else setAudioOverviewProgressHandler(handler);

    return () => {
      if (player) player.setProgressHandler(null);
      else setAudioOverviewProgressHandler(null);
    };
  }, [dispatch, player, store]);

  useAudioOverviewConfigUpkeep({
    status: configState.status,
    config: configState.value,
    dispatch,
    onLocked: () => stopForPlan('plan_changed'),
  });

  // Once per session (and again when the plan changes): pick up overviews
  // that were generating when the app was last closed, so they get watched.
  useEffect(() => {
    if (!isAvailable) return;
    const requestedAt = nextAudioOverviewStamp();

    listAudioOverviews({ limit: BOOT_FETCH_LIMIT }).then(
      (page) =>
        dispatch(audioOverviewsUpserted({ items: page.items, requestedAt })),
      () => undefined
    );
  }, [dispatch, isAvailable, listEpoch]);

  useAudioOverviewPolling({ pendingKey, enabled: isAvailable, dispatch });

  // Toast the overviews this session watched finish.
  useEffect(() => {
    const lastStatus = lastStatusRef.current;
    let didFinish = false;

    const requestedArticleIds =
      store.getState().audioOverviews.requestedArticleOverviewIds;

    Object.values(entities).forEach((overview) => {
      const previous = lastStatus.get(overview.id);
      lastStatus.set(overview.id, overview.status);
      if (
        !previous ||
        isAudioOverviewTerminal(previous) ||
        !isAudioOverviewTerminal(overview.status)
      ) {
        return;
      }

      // An article's shared episode toasts only for whoever asked for it
      // this session, not for a student who just opened the article.
      if (overview.source === 'article' && !requestedArticleIds[overview.id]) {
        return;
      }

      didFinish = true;
      if (overview.status === 'READY') {
        showToast(overview, 'ready');
      } else {
        showToast(overview, 'failed');
      }
    });

    // In-progress counts moved; keep the form's usage line honest.
    if (didFinish) void loadAudioOverviewConfig(dispatch);
    // Keyed on the entities alone: `showToast` is rebuilt every render.
  }, [entities]);

  if (!isUpgradeOpen) return null;

  return (
    <Suspense fallback={null}>
      <UpgradeModal
        open={isUpgradeOpen}
        close={() => {
          dispatch(audioOverviewUpgradePromptClosed());
          // They may have just upgraded in the pricing tab.
          void loadAudioOverviewConfig(dispatch);
        }}
        contentType={
          upgradeSourceKind === 'article'
            ? 'Article Audio Overview'
            : 'Audio Overview'
        }
      />
    </Suspense>
  );
};

export default AudioOverviewWatcher;
